import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { Download, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { generateMarkdownPDF } from '@/lib/pdf';
import {
  adPerformanceReportQueryOptions,
  metaCampaignOverviewQueryOptions,
} from '@/lib/query-options';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { useScopedCampaigns, classifyCampaign } from '@/hooks/usePerformanceData';
import { resolveDateWindow, todayISO } from '@/lib/performance/date-window';
import { toDisplayAmount } from '@/lib/performance/money';
import { platformsInScope, type PlatformScope } from '@/lib/performance/types';
import { parseCampaignName } from '../cells';
import {
  campaignsDataAtom,
  organizationDataAtom,
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceCustomRangeAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
} from '@/store';

const fmtInt = (n: number) => Math.round(n).toLocaleString('en-GB');
const fmtMoney = (n: number, cs: string) => `${cs}${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * #6501 — Export the consolidated Performance view as a branded PDF. Sits to
 * the right of the filters; defaults to ALL the organisation's campaigns over
 * the page's current window, with its own date pickers and a switch to honour
 * the page's campaign narrowing instead. Client-side through the shared
 * jsPDF markdown renderer (the reports pipeline).
 */
export function ExportPdfButton({ scope }: { scope: PlatformScope }) {
  const queryClient = useQueryClient();
  const organizationData = useAtomValue(organizationDataAtom);
  const campaigns = useAtomValue(campaignsDataAtom);
  const { symbol } = useCurrencySymbol();
  const cs = symbol ?? '';

  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const customRange = useAtomValue(consolidatedPerformanceCustomRangeAtom);
  const pageWindow = useMemo(() => resolveDateWindow(selectedDays, customRange), [selectedDays, customRange]);

  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);
  const { scopedGoogleAdsIds } = useScopedCampaigns({ includeExternal, typeFilter, selectedCampaignIds });

  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [allCampaigns, setAllCampaigns] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const googleInScope = platformsInScope(scope).includes('google');
  const metaInScope = platformsInScope(scope).includes('meta');

  const allGoogleIds = useMemo(
    () => campaigns.map((c) => c.googleads_campaign?.googleads_campaign_id).filter(Boolean).join(','),
    [campaigns],
  );

  const runExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const start = from || pageWindow.start_date;
      const end = to || pageWindow.end_date;
      const ids = allCampaigns ? allGoogleIds : scopedGoogleAdsIds;

      const googleRows =
        googleInScope && ids
          ? await queryClient.fetchQuery(adPerformanceReportQueryOptions(ids, start, end))
          : [];
      const metaResp = metaInScope
        ? await queryClient.fetchQuery(metaCampaignOverviewQueryOptions(true)).catch(() => null)
        : null;

      type Row = { name: string; platform: string; type: string; impressions: number; clicks: number; conversions: number; spend: number };
      const byCampaign = new Map<string, Row>();
      for (const ad of googleRows ?? []) {
        const key = `g:${ad.campaign_id}`;
        const row = byCampaign.get(key) ?? {
          name: parseCampaignName(ad.campaign_name || ad.campaign_id),
          platform: 'Google',
          type: '',
          impressions: 0,
          clicks: 0,
          conversions: 0,
          spend: 0,
        };
        row.impressions += ad.impressions ?? 0;
        row.clicks += ad.clicks ?? 0;
        row.conversions += ad.conversions ?? 0;
        row.spend += ad.cost ?? 0;
        byCampaign.set(key, row);
      }
      const typeByGid = new Map<string, string>();
      for (const c of campaigns) {
        const gid = c.googleads_campaign?.googleads_campaign_id;
        if (gid) typeByGid.set(parseCampaignName(c.name), classifyCampaign(c));
      }
      for (const row of byCampaign.values()) row.type = typeByGid.get(row.name) ?? '';
      for (const m of metaResp?.rows ?? []) {
        byCampaign.set(`m:${m.meta_campaign_id}`, {
          name: m.name ?? String(m.meta_campaign_id),
          platform: 'Meta',
          type: '',
          impressions: m.impressions ?? 0,
          clicks: m.clicks ?? 0,
          conversions: m.leads ?? 0,
          spend: toDisplayAmount(m.spend_cents ?? 0, 'meta'),
        });
      }

      const rows = Array.from(byCampaign.values()).sort((a, b) => b.spend - a.spend);
      const totals = rows.reduce(
        (acc, r) => ({
          impressions: acc.impressions + r.impressions,
          clicks: acc.clicks + r.clicks,
          conversions: acc.conversions + r.conversions,
          spend: acc.spend + r.spend,
        }),
        { impressions: 0, clicks: 0, conversions: 0, spend: 0 },
      );

      const scopeLabel = scope === 'all' ? 'All platforms' : scope === 'google' ? 'Google' : 'Meta';
      const md = [
        `# Performance`,
        ``,
        `${organizationData?.name ?? ''} · ${start} to ${end} · ${scopeLabel} · ${allCampaigns ? 'All campaigns' : 'Filtered campaigns'}`,
        ``,
        `## Totals`,
        ``,
        `| Cost | Impressions | Clicks | Conversions |`,
        `| --- | --- | --- | --- |`,
        `| ${fmtMoney(totals.spend, cs)} | ${fmtInt(totals.impressions)} | ${fmtInt(totals.clicks)} | ${fmtInt(totals.conversions)} |`,
        ``,
        `## Campaigns`,
        ``,
        `| Campaign | Platform | Impressions | Clicks | Conversions | Cost | CPC | CPA |`,
        `| --- | --- | --- | --- | --- | --- | --- | --- |`,
        ...rows.map((r) => {
          const cpc = r.clicks > 0 ? r.spend / r.clicks : 0;
          const cpa = r.conversions > 0 ? r.spend / r.conversions : 0;
          return `| ${r.name} | ${r.platform} | ${fmtInt(r.impressions)} | ${fmtInt(r.clicks)} | ${fmtInt(r.conversions)} | ${fmtMoney(r.spend, cs)} | ${fmtMoney(cpc, cs)} | ${fmtMoney(cpa, cs)} |`;
        }),
        ``,
        metaInScope ? `Meta figures cover the synced insights window.` : ``,
      ].join('\n');

      const blob = await generateMarkdownPDF({ content: md, headerLabel: 'Performance export' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `performance-${(organizationData?.name ?? 'export').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${start}-to-${end}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The export failed. Try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setFrom(pageWindow.start_date);
          setTo(pageWindow.end_date);
          setError(null);
        }
      }}
    >
      <PopoverTrigger asChild>
        {/* The pre-existing export idiom (Download icon + the single word), the
            same face as ExportCsvButton on the table tabs. */}
        <Button variant="outline" className="h-9 gap-1.5 text-gray-900">
          <Download className="h-4 w-4" />
          Export
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3">
        <p className="mb-2 text-xs text-gray-500">Export window</p>
        <div className="space-y-2">
          <label className="block text-xs text-gray-500">
            From
            <input
              type="date"
              value={from}
              max={to && to < todayISO() ? to : todayISO()}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-0.5 w-full rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-[#290BD9] focus:outline-none"
            />
          </label>
          <label className="block text-xs text-gray-500">
            To
            <input
              type="date"
              value={to}
              min={from || undefined}
              max={todayISO()}
              onChange={(e) => setTo(e.target.value)}
              className="mt-0.5 w-full rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-[#290BD9] focus:outline-none"
            />
          </label>
          <label className="flex cursor-pointer items-center gap-2 pt-1">
            <Checkbox
              checked={allCampaigns}
              onCheckedChange={(v) => setAllCampaigns(v === true)}
              className="data-[state=checked]:bg-[#290BD9] data-[state=checked]:border-[#290BD9]"
            />
            <span className="text-sm text-gray-900">All campaigns</span>
          </label>
          <p className="text-xs text-gray-500">
            {allCampaigns ? 'Every campaign in the organisation.' : 'Honours the page campaign filters.'}
          </p>
          {error && (
            <div className="border-l-4 border-[#FCA5A5] bg-[#FEE2E2] px-2 py-1.5 text-xs text-[#B91C1C]">{error}</div>
          )}
          <Button
            onClick={() => void runExport()}
            disabled={exporting || !from || !to || from > to || to > todayISO()}
            className="h-9 w-full bg-[#290BD9] text-white hover:bg-[#2309C5]"
          >
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {exporting ? 'Exporting' : 'Export PDF'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
